import React from 'react';
import { AlertTriangle, X, Trash2 } from 'lucide-react';

/**
 * Dependency Delete Dialog Component
 * Shows a confirmation dialog when deleting records with dependencies
 */
const DependencyDeleteDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  entityName,
  dependencies 
}) => {
  if (!isOpen) return null;

  const getEntityTypeLabel = (type) => {
    const labels = {
      'Leads': 'Lead',
      'Opportunities': 'Opportunity',
      'Contracts': 'Contract',
      'Invoices': 'Invoice',
      'Activities': 'Activity',
      'LineMessages': 'LINE Message',
      'EmailMessages': 'Email Message',
      'BusinessCardInputs': 'Business Card',
      'PipelineReminders': 'Pipeline Reminder',
      'DealLossReasons': 'Deal Loss Reason'
    };
    return labels[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-yellow-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              Confirm Deletion with Dependencies
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="mb-4">
            <p className="text-gray-700 mb-4">
              This {entityName} has dependent records that will also be deleted:
            </p>
            
            {dependencies?.message && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 font-medium">{dependencies.message}</p>
              </div>
            )}

            {/* Dependency List */}
            {dependencies?.items && dependencies.items.length > 0 && (
              <div className="space-y-4">
                {dependencies.items.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">
                        {getEntityTypeLabel(item.type)}
                      </h3>
                      <span className="text-sm text-gray-600">
                        {item.count} {item.count === 1 ? 'record' : 'records'}
                      </span>
                    </div>
                    
                    {item.records && item.records.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto">
                        <ul className="space-y-1">
                          {item.records.slice(0, 10).map((record, recordIndex) => (
                            <li key={recordIndex} className="text-sm text-gray-600 flex items-center">
                              <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                              <span className="truncate">
                                {record.name || `${getEntityTypeLabel(item.type)} #${record.id}`}
                              </span>
                              {record.status && (
                                <span className="ml-2 text-xs text-gray-500">
                                  ({record.status})
                                </span>
                              )}
                            </li>
                          ))}
                          {item.records.length > 10 && (
                            <li className="text-sm text-gray-500 italic">
                              ... and {item.records.length - 10} more
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">
                ⚠️ Warning: This action cannot be undone. All dependent records will be permanently deleted.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete All</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DependencyDeleteDialog;

